import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingDown, TrendingUp, Wallet, Percent } from 'lucide-react'

import { formatMoney } from '@/lib/format'
import type { PnL } from '@/types/finance'

function StatCard({
  title,
  value,
  hint,
  icon: Icon,
  tone,
  loading,
}: {
  title: string
  value: string
  hint?: string
  icon: React.ComponentType<{ className?: string }>
  tone?: 'positive' | 'negative'
  loading?: boolean
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="mt-1.5 h-7 w-28" />
            ) : (
              <p
                className={`truncate text-2xl font-bold ${
                  tone === 'positive'
                    ? 'text-success'
                    : tone === 'negative'
                      ? 'text-destructive'
                      : ''
                }`}
              >
                {value}
              </p>
            )}
            {hint && !loading && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
          </div>
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  )
}

interface StatCardsProps {
  pnl?: PnL
  priorPnl?: PnL
  isLoading: boolean
}

// KPI row: revenue, expenses, net income and margin, each with the change
// against the comparable prior period.
export function StatCards({ pnl, priorPnl, isLoading }: StatCardsProps) {
  const delta = (current?: number, prior?: number) => {
    if (current === undefined || prior === undefined || prior === 0) return undefined
    return ((current - prior) / prior) * 100
  }
  const fmtDelta = (d?: number) =>
    d === undefined
      ? undefined
      : `${d >= 0 ? '+' : ''}${d.toFixed(0)}% vs prior period`

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Total revenue"
        value={formatMoney(pnl?.total_revenue ?? 0)}
        hint={fmtDelta(delta(pnl?.total_revenue, priorPnl?.total_revenue))}
        icon={TrendingUp}
        loading={isLoading}
      />
      <StatCard
        title="Total expenses"
        value={formatMoney(pnl?.total_expenses ?? 0)}
        hint={fmtDelta(delta(pnl?.total_expenses, priorPnl?.total_expenses))}
        icon={TrendingDown}
        loading={isLoading}
      />
      <StatCard
        title="Net income"
        value={formatMoney(pnl?.net_income ?? 0)}
        hint={fmtDelta(delta(pnl?.net_income, priorPnl?.net_income))}
        icon={Wallet}
        tone={pnl && pnl.net_income < 0 ? 'negative' : pnl && pnl.net_income > 0 ? 'positive' : undefined}
        loading={isLoading}
      />
      <StatCard
        title="Margin"
        value={`${(pnl?.margin_pct ?? 0).toFixed(1)}%`}
        icon={Percent}
        loading={isLoading}
      />
    </div>
  )
}
